import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faXmark } from '@fortawesome/free-solid-svg-icons';
import usePresetSubscription from 'modules/Common/hooks/usePresetSubscription';

// Trailing chunk of every SmartSegments row — renders user-saved
// presets (loadable, deletable) and the "Save current view" pill,
// driven by the same preset architecture CgSearchFilter exposes via
// the `cgTableFilterReady` event. Keeps the segments rows on Imports /
// Suppliers / Installations / Emissions / Benchmarks visually
// identical without duplicating the wiring 5×.
//
// Each saved preset reuses the SegmentPill look from the page-specific
// segments above so the row reads as one continuous control:
//   [built-in segments]  |  [saved-preset pills]  |  [Save current view]

const PresetPill = ({ active, label, onClick, onRemove }) => {
  const { t } = useTranslation('common');
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${
        active
          ? 'bg-white text-slate-900 border border-slate-300 shadow-sm'
          : 'text-slate-500 hover:text-slate-700 border hover:bg-slate-100'
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className="inline-flex items-center gap-1.5 outline-none"
      >
        <FontAwesomeIcon
          icon={faBookmark}
          className="text-[11px]"
        />
        {label}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove?.();
        }}
        aria-label={t('common:filters.removePreset', {
          name: label,
          defaultValue: 'Remove {{name}}',
        })}
        className="text-slate-400 hover:text-red-500 transition-colors"
      >
        <FontAwesomeIcon
          icon={faXmark}
          className="text-[10px]"
        />
      </button>
    </span>
  );
};

export default function PresetSegmentsTail() {
  const { t } = useTranslation('common');
  const {
    savedPresets,
    matchingPresetId,
    canSaveCurrentView,
    onSavePreset,
    handleLoadPreset,
    handleDeletePreset,
  } = usePresetSubscription();

  return (
    <>
      {savedPresets.length > 0 && <span className="text-slate-200 mx-1">|</span>}

      {savedPresets.map((preset) => (
        <PresetPill
          key={preset.id}
          active={matchingPresetId === preset.id}
          label={preset.name}
          onClick={() => handleLoadPreset?.(preset.id)}
          onRemove={() => handleDeletePreset?.(preset.id)}
        />
      ))}

      <span className="text-slate-200 mx-1">|</span>
      <button
        type="button"
        onClick={canSaveCurrentView ? onSavePreset : undefined}
        disabled={!canSaveCurrentView}
        title={
          canSaveCurrentView
            ? t('common:filters.saveCurrentViewTitle', 'Save current filters as a named view')
            : t(
                'common:filters.saveCurrentViewDisabledTitle',
                'Apply at least one filter to save a view',
              )
        }
        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-dashed transition-colors ${
          canSaveCurrentView
            ? 'border-primaryColor/40 text-primaryColor hover:bg-primaryColor/5 cursor-pointer'
            : 'border-slate-300 text-slate-400 cursor-not-allowed'
        }`}
      >
        <FontAwesomeIcon
          icon={faBookmark}
          className="text-[11px]"
        />
        {t('common:filters.saveCurrentView', 'Save current view')}
      </button>
    </>
  );
}
